/* ============================================================
 * Sticky Add to Cart bar
 *
 * Stepwise flow:
 *   1. Bar shows option #1 chips + qty + ATC.
 *   2. Click ATC → advances to option #2 (chips + ATC + close X).
 *   3. ... advance through all options.
 *   4. On final step, ATC clicks the main product's ATC button so the
 *      existing theme JS + any third-party cart drawer handle the add.
 *
 * Chips and qty are kept in sync with the main product form's variant
 * radios and quantity input (bi-directional).
 * ============================================================ */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function init(bar) {
    if (!bar || bar.dataset.stickyAtcReady === 'true') return;
    bar.dataset.stickyAtcReady = 'true';

    var productSection = document.querySelector('[data-section-type="product"]');
    if (!productSection) return;

    var mainForm = productSection.querySelector('form[action*="/cart/add"]');
    var mainAtcBtn = productSection.querySelector('[data-add-to-cart]');

    // Locate (or create) the form's quantity input. This template doesn't
    // include a `quantity_selector` block, so without us injecting one the form
    // submits with no quantity field and Shopify defaults to qty=1 — meaning
    // both the sticky ATC and the main ATC would always add only 1.
    var mainQtyInput = (function () {
      if (!mainForm) return null;
      // 1. Inside the form
      var insideForm = mainForm.querySelector('input[name="quantity"]');
      if (insideForm) return insideForm;
      // 2. Externally associated via form="<formId>"
      var formId = mainForm.id;
      if (formId) {
        var external = document.querySelector('input[name="quantity"][form="' + formId + '"]');
        if (external) return external;
      }
      // 3. None exists — inject one. The form's serializer reads field.value, so
      //    keeping this hidden and updating .value is sufficient.
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      hidden.value = '1';
      hidden.setAttribute('data-sticky-atc-injected-qty', 'true');
      mainForm.appendChild(hidden);
      return hidden;
    })();

    // Variant data — used to compute availability ourselves (independent of
    // the theme's timing) so chips for sold-out combos can be disabled.
    var variants = [];
    var variantJson = productSection.querySelector('[data-variant-json]');
    if (variantJson) {
      try { variants = JSON.parse(variantJson.textContent || variantJson.value || '[]'); }
      catch (err) { variants = []; }
    }

    var steps = bar.querySelectorAll('[data-sticky-atc-step]');
    var totalSteps = parseInt(bar.dataset.totalSteps || '0', 10);
    var closeBtn = bar.querySelector('[data-sticky-atc-close]');
    var chips = bar.querySelectorAll('[data-sticky-atc-chip]');
    var qtyInput = bar.querySelector('[data-sticky-atc-qty-input]');
    var qtyDown = bar.querySelector('[data-sticky-atc-qty-down]');
    var qtyUp = bar.querySelector('[data-sticky-atc-qty-up]');
    var atcButtons = bar.querySelectorAll('[data-sticky-atc-add]');

    var enableDesktop = bar.dataset.enableDesktop === 'true';
    var enableMobile = bar.dataset.enableMobile === 'true';
    var mobileAlwaysVisible = bar.dataset.mobileAlwaysVisible === 'true';

    function isMobile() {
      return window.matchMedia('(max-width: 768px)').matches;
    }

    /* ---------------- Chip layout: wrap if ≤2 rows fit, else carousel ----------------
       Mobile only. Desktop always uses the single-line/scroll fallback. */
    function applyChipLayoutFor(group) {
      if (!group) return;
      if (!isMobile()) {
        group.classList.remove('is-wrapped');
        return;
      }
      // Force wrap temporarily to measure.
      group.classList.add('is-wrapped');
      var chips = group.querySelectorAll('.sticky-atc__chip');
      var rowTops = {};
      chips.forEach(function (c) { rowTops[c.offsetTop] = true; });
      var rowCount = Object.keys(rowTops).length;
      if (rowCount > 2) {
        group.classList.remove('is-wrapped');
      }
    }

    function applyChipLayoutToActive() {
      var activeStep = bar.querySelector('.sticky-atc__step.is-active');
      if (!activeStep) return;
      var group = activeStep.querySelector('.sticky-atc__chips');
      applyChipLayoutFor(group);
    }

    /* ---------------- Step navigation ---------------- */
    function goToStep(idx) {
      if (idx < 0) idx = 0;
      if (idx >= totalSteps && totalSteps > 0) idx = totalSteps - 1;
      bar.dataset.currentStep = String(idx);
      steps.forEach(function (step) {
        var stepIdx = parseInt(step.dataset.stickyAtcStep || '0', 10);
        step.classList.toggle('is-active', stepIdx === idx);
      });
      if (closeBtn) {
        if (idx > 0) closeBtn.removeAttribute('hidden');
        else closeBtn.setAttribute('hidden', '');
      }
      // Layout the chips now that the new step's group is visible (offsetTop
      // measurement only works on rendered/visible elements).
      applyChipLayoutToActive();
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', function () { goToStep(0); });
    }

    /* ---------------- Chip → main radio sync ----------------
       Variant radios use `form="..."` attribute association, so they live
       OUTSIDE the <form> element. Search the whole product section. */
    function getAllVariantRadios() {
      return productSection.querySelectorAll('[data-variant-input]');
    }

    function getMainRadio(optionPosition, value) {
      var radios = getAllVariantRadios();
      for (var i = 0; i < radios.length; i++) {
        var radio = radios[i];
        var dataIdx = radio.getAttribute('data-index'); // "option1", "option2", ...
        var idxNum = dataIdx ? parseInt(dataIdx.replace('option', ''), 10) : null;
        if (idxNum === optionPosition && radio.value === value) return radio;
      }
      return null;
    }

    /* ---------------- Selected values (source of truth: checked variant radios) ---------------- */
    function getSelectedValues() {
      var selected = {};
      productSection.querySelectorAll('[data-variant-input]:checked').forEach(function (r) {
        var dataIdx = r.getAttribute('data-index');
        var idxNum = dataIdx ? parseInt(dataIdx.replace('option', ''), 10) : null;
        if (idxNum != null) selected[idxNum] = r.value;
      });
      return selected;
    }

    /* ---------------- Availability calculation from variants JSON ---------------- */
    function isValueAvailable(optionPosition, value) {
      if (!variants || !variants.length) return true; // unknown — fail open
      var selected = getSelectedValues();
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (!v.available) continue;
        if (v['option' + optionPosition] !== value) continue;
        // For options before this one, the variant must match the currently
        // selected value (mirrors the theme's setAvailability logic).
        var matchesPrior = true;
        for (var p = 1; p < optionPosition; p++) {
          if (selected[p] && v['option' + p] !== selected[p]) { matchesPrior = false; break; }
        }
        if (!matchesPrior) continue;
        return true;
      }
      return false;
    }

    function refreshChipAvailability() {
      bar.querySelectorAll('[data-sticky-atc-chip]').forEach(function (chip) {
        var optionPosition = parseInt(chip.dataset.optionPosition, 10);
        var value = chip.dataset.optionValue;
        var available = isValueAvailable(optionPosition, value);
        chip.classList.toggle('is-unavailable', !available);
      });
    }

    /* ---------------- Chip selection sync (from main form → chips) ---------------- */
    function refreshChipSelection() {
      var selected = getSelectedValues();
      bar.querySelectorAll('[data-sticky-atc-chip]').forEach(function (chip) {
        var optionPosition = parseInt(chip.dataset.optionPosition, 10);
        var value = chip.dataset.optionValue;
        chip.classList.toggle('is-selected', selected[optionPosition] === value);
      });
    }

    /* ---------------- Qty sync (from main form → sticky) ---------------- */
    function refreshStickyQty() {
      if (!mainQtyInput || !qtyInput) return;
      var v = parseInt(mainQtyInput.value, 10);
      if (!isNaN(v) && v >= 1 && qtyInput.value !== String(v)) {
        qtyInput.value = v;
      }
    }

    /* ---------------- Master sync from main form ---------------- */
    function syncFromMain() {
      refreshChipSelection();
      refreshChipAvailability();
      refreshStickyQty();
    }

    /* ---------------- Chip click → main radio ---------------- */
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        // Unavailable combos are non-selectable.
        if (chip.classList.contains('is-unavailable')) return;

        var optionPosition = parseInt(chip.dataset.optionPosition, 10);
        var value = chip.dataset.optionValue;

        // Optimistic UI: mark this chip as selected immediately for instant feedback.
        var group = chip.parentNode.querySelectorAll('[data-sticky-atc-chip]');
        group.forEach(function (sibling) { sibling.classList.remove('is-selected'); });
        chip.classList.add('is-selected');

        // Click the matching main radio's <label> — that's the most native path:
        // the browser checks the radio, fires input + change, which runs the
        // theme's variant change handler (price, availability, etc.).
        var radio = getMainRadio(optionPosition, value);
        if (radio) {
          var label = document.querySelector('label[for="' + radio.id + '"]');
          if (label) {
            label.click();
          } else {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        // Pull the final state back so availability flips for other options.
        setTimeout(syncFromMain, 0);
      });
    });

    /* ---------------- Main form → sticky sync listeners ----------------
       Listen on the product section (not the <form>) since variant radios
       live outside the form element. */
    productSection.addEventListener('change', function (e) {
      if (!e.target) return;
      if (e.target.matches && e.target.matches('[data-variant-input]')) {
        setTimeout(syncFromMain, 0);
      }
    });

    // Defensive observer: catches programmatic checked/class changes that
    // don't bubble a `change` event.
    var radioObserver = new MutationObserver(function () { syncFromMain(); });
    getAllVariantRadios().forEach(function (radio) {
      radioObserver.observe(radio, { attributes: true, attributeFilter: ['class', 'disabled', 'checked'] });
    });

    // Initial sync after a tick (theme finishes its own init).
    setTimeout(syncFromMain, 0);
    setTimeout(syncFromMain, 250);
    setTimeout(syncFromMain, 800);

    /* ---------------- Quantity two-way sync ---------------- */
    function setQty(val, originIsSticky) {
      val = Math.max(1, parseInt(val, 10) || 1);
      if (qtyInput && qtyInput.value !== String(val)) qtyInput.value = val;
      if (mainQtyInput && mainQtyInput.value !== String(val)) {
        mainQtyInput.value = val;
        // Notify the theme (cart-drawer plugins watch input/change events).
        mainQtyInput.dispatchEvent(new Event('input',  { bubbles: true }));
        mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (qtyUp) qtyUp.addEventListener('click', function () {
      setQty((parseInt(qtyInput.value, 10) || 1) + 1, true);
    });
    if (qtyDown) qtyDown.addEventListener('click', function () {
      setQty((parseInt(qtyInput.value, 10) || 1) - 1, true);
    });
    if (qtyInput) {
      qtyInput.addEventListener('input',  function () { setQty(qtyInput.value, true); });
      qtyInput.addEventListener('change', function () { setQty(qtyInput.value, true); });
    }

    // Main qty → sticky qty
    if (mainQtyInput && qtyInput) {
      ['input', 'change'].forEach(function (ev) {
        mainQtyInput.addEventListener(ev, function () {
          var v = parseInt(mainQtyInput.value, 10);
          if (!isNaN(v) && v >= 1 && qtyInput.value !== String(v)) {
            qtyInput.value = v;
          }
        });
      });
      // The theme's QtyAdjuster updates the input via .value =; some browsers
      // don't fire input for that. Watch the value attribute and parent clicks
      // as a backstop.
      var mainQtyWrapper = mainQtyInput.closest('.js-qty__wrapper');
      if (mainQtyWrapper) {
        mainQtyWrapper.addEventListener('click', function () {
          setTimeout(function () {
            var v = parseInt(mainQtyInput.value, 10);
            if (!isNaN(v) && v >= 1 && qtyInput.value !== String(v)) qtyInput.value = v;
          }, 0);
        });
      }
    }

    /* ---------------- ATC button click handler ---------------- */
    atcButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (btn.disabled) return;

        var stepIdx = parseInt(btn.dataset.stepIndex || '0', 10);

        // Not the last step → advance.
        if (totalSteps > 1 && stepIdx < totalSteps - 1) {
          goToStep(stepIdx + 1);
          return;
        }

        // Last step (or single-option / no-option product) → trigger main ATC.
        if (!mainAtcBtn) return;
        btn.classList.add('is-loading');
        mainAtcBtn.click();
      });
    });

    // Mirror main ATC's disabled state onto every sticky ATC button.
    if (mainAtcBtn) {
      var atcObserver = new MutationObserver(function () {
        var soldOut = mainAtcBtn.disabled;
        atcButtons.forEach(function (btn) {
          if (soldOut) {
            btn.setAttribute('disabled', 'disabled');
            btn.classList.add('is-disabled');
          } else {
            btn.removeAttribute('disabled');
            btn.classList.remove('is-disabled');
            btn.classList.remove('is-loading');
          }
          // Update label
          var stickyAtcText = btn.querySelector('[data-sticky-atc-add-text]');
          if (stickyAtcText) {
            var defaultText = stickyAtcText.dataset.defaultText || '';
            var soldOutText = stickyAtcText.dataset.soldOutText || '';
            stickyAtcText.textContent = soldOut ? soldOutText : defaultText;
          }
        });
      });
      atcObserver.observe(mainAtcBtn, { attributes: true, attributeFilter: ['class', 'disabled'], childList: true, subtree: true });
    }

    // When add succeeds, reset to first step and clear loading.
    if (mainForm) {
      mainForm.addEventListener('ajaxProduct:added', function () {
        atcButtons.forEach(function (b) { b.classList.remove('is-loading'); });
        goToStep(0);
      });
    }
    // Some third-party carts intercept the click and don't fire ajaxProduct:added.
    // Clear loading after a safety timeout.
    atcButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setTimeout(function () { btn.classList.remove('is-loading'); }, 3000);
      });
    });

    /* ---------------- Visibility ---------------- */
    var triggerEl =
      productSection.querySelector('.payment-buttons') ||
      productSection.querySelector('.add-to-cart');

    function applyVisibility() {
      var mobile = isMobile();
      var shouldShow;
      if (mobile && !enableMobile) shouldShow = false;
      else if (!mobile && !enableDesktop) shouldShow = false;
      else if (mobile && mobileAlwaysVisible) shouldShow = true;
      else if (!triggerEl) shouldShow = true;
      else {
        var rect = triggerEl.getBoundingClientRect();
        var inView = rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
        shouldShow = !inView;
      }
      bar.classList.toggle('is-visible', shouldShow);
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      updateFloatingWidgetOffsets();
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () { applyVisibility(); ticking = false; });
        ticking = true;
      }
    }, { passive: true });
    window.addEventListener('resize', function () {
      applyVisibility();
      applyChipLayoutToActive();
    });
    applyVisibility();
    // Re-measure after fonts settle (chip widths can shift slightly post-load).
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(applyChipLayoutToActive);
    }

    /* ---------------- Floating chat widgets (Zoko WhatsApp etc.) ----------------
       Push them up by the bar's height when the bar is visible so they don't
       overlap the close × or the ATC button. */
    var widgetOriginalBottoms = new WeakMap();
    function getFloatingWidgets() {
      // Zoko: <div id="zoko-..."><div style="position:fixed; bottom:50px; right:10px; ...">
      // We also handle a couple of other common fixed-position chat widgets.
      var selectors = [
        '[id^="zoko-"] > div',
        '.zoko_popup_campaign',
        '.tidio-widget',
        '#fb-root',
        '.crisp-client',
        '[id^="whatsapp-chat"]',
        '.wa-chat-box'
      ];
      var nodes = [];
      selectors.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          var cs = window.getComputedStyle(el);
          if (cs.position === 'fixed' && cs.bottom && cs.bottom !== 'auto') {
            nodes.push(el);
          }
        });
      });
      return nodes;
    }

    function updateFloatingWidgetOffsets() {
      var widgets = getFloatingWidgets();
      var visible = bar.classList.contains('is-visible');
      var lift = visible ? (bar.offsetHeight + 12) : 0;
      widgets.forEach(function (el) {
        // Capture the very first observed bottom value so re-runs don't keep
        // adding on top of our previous lift.
        if (!widgetOriginalBottoms.has(el)) {
          var originalInline = el.style.bottom;
          if (originalInline) {
            widgetOriginalBottoms.set(el, originalInline);
          } else {
            var computed = parseInt(window.getComputedStyle(el).bottom, 10);
            widgetOriginalBottoms.set(el, isNaN(computed) ? '' : computed + 'px');
          }
        }
        var orig = widgetOriginalBottoms.get(el);
        if (lift > 0) {
          var origParsed = parseInt(orig, 10);
          var base = isNaN(origParsed) ? 0 : origParsed;
          el.style.setProperty('bottom', (base + lift) + 'px', 'important');
        } else {
          if (orig === '') el.style.removeProperty('bottom');
          else el.style.setProperty('bottom', orig, 'important');
        }
      });
    }

    // Re-run when bar height changes (step changes can resize the bar).
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(updateFloatingWidgetOffsets);
      ro.observe(bar);
    }
    // Re-run when widgets load asynchronously after our init.
    var widgetReadyAttempts = 0;
    var widgetReadyInterval = setInterval(function () {
      widgetReadyAttempts++;
      updateFloatingWidgetOffsets();
      if (widgetReadyAttempts > 20) clearInterval(widgetReadyInterval);
    }, 500);
    window.addEventListener('beforeunload', function () { clearInterval(widgetReadyInterval); });

    /* ---------------- Initial state ---------------- */
    goToStep(0);
  }

  ready(function () {
    document.querySelectorAll('[data-sticky-atc]').forEach(init);
  });

  document.addEventListener('shopify:section:load', function (e) {
    if (!e || !e.target) return;
    var bar = e.target.querySelector('[data-sticky-atc]');
    if (bar) init(bar);
  });
})();
